import { BrowserRouter, Navigate, NavLink, Route, Routes } from "react-router-dom";
import { lazy, memo, Suspense, useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HeroUIProvider } from "@heroui/react";

import { ChangePasswordModal } from "./components/ChangePasswordModal";
import { ToastContainer } from "./components/ToastContainer";
import { LoginPage } from "./pages/LoginPage";
import { authApi } from "./api/authApi";

const LazyBotsView = lazy(() =>
  import("./features/bots/BotsView").then(m => ({ default: m.BotsView }))
);

const LazyStudentsPage = lazy(() =>
  import("./pages/StudentsPage").then(m => ({ default: m.StudentsPage }))
);

const LazyScenariosPage = lazy(() =>
  import("./pages/ScenariosPage").then(m => ({ default: m.ScenariosPage }))
);

const LazyAdminUsersPage = lazy(() => import("./pages/AdminUsersPage"));

const LazyBotTokensPage = lazy(() =>
  import("./pages/BotTokensPage").then(m => ({ default: m.BotTokensPage }))
);

const LazyFeedbackPage = lazy(() =>
  import("./pages/FeedbackPage").then(m => ({ default: m.FeedbackPage }))
);

const sectionNavClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-md px-4 py-2 text-sm font-medium transition-colors ${
    isActive ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
  }`;

const MainContent = memo(function MainContent() {
  return (
    <Suspense fallback={<div className="text-center py-8 text-gray-500">Загрузка...</div>}>
      <Routes>
        <Route path="/" element={<LazyBotsView />} />
        <Route path="/scenarios" element={<LazyScenariosPage />} />
        <Route path="/students" element={<LazyStudentsPage />} />
        <Route path="/tokens" element={<LazyBotTokensPage />} />
        <Route path="/users" element={<LazyAdminUsersPage />} />
        <Route path="/feedback" element={<LazyFeedbackPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
});

function AppInner() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const queryClient = useQueryClient();

  // Проверка статуса авторизации (проверяем наличие токена)
  const { data: authStatus } = useQuery({
    queryFn: authApi.getStatus,
    queryKey: ["auth-status"],
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { data: authMe } = useQuery({
    enabled: authChecked && isAuthenticated,
    queryFn: authApi.getMe,
    queryKey: ["auth-me"],
    refetchOnWindowFocus: true,
    retry: false,
  });

  const role = authMe?.role?.toLowerCase();
  const isAdmin = role === "admin";
  const canViewStudents = role === "admin" || role === "developer";

  // Мутация для выхода
  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      setIsAuthenticated(false);
      queryClient.removeQueries({ queryKey: ["auth-me"] });
      queryClient.invalidateQueries({ queryKey: ["auth-status"] });
    },
  });

  useEffect(() => {
    if (authStatus) {
      setAuthChecked(true);
      setIsAuthenticated(authStatus.authenticated);
    }
  }, [authStatus]);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="text-xl text-gray-600">Загрузка...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <LoginPage onLoginSuccess={handleLoginSuccess} />
        <ToastContainer />
      </>
    );
  }

  return (
    <>
      <div className="megafon-surface min-h-screen">
        <header className="border-b border-purple-200 bg-gradient-to-r from-purple-700 to-green-500 shadow-sm">
          <div className="max-w-[120rem] mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center gap-4">
              <div className="flex items-center">
                <img
                  className="w-auto"
                  style={{ height: "60px", mixBlendMode: "lighten" }}
                  src="/mega_coach_logo.svg"
                  alt="Mega Coach"
                />
              </div>
              <div className="flex shrink-0 items-center gap-2 sm:gap-4">
                {authMe && (
                  <div className="hidden text-right text-sm sm:block">
                    <div className="font-medium text-white">{authMe.username}</div>
                    <div className="text-xs text-purple-200 capitalize">{authMe.role}</div>
                  </div>
                )}
                <button
                  className="px-3 py-2 rounded-md font-medium text-sm border border-white/30 bg-white/10 text-white hover:bg-white/20 active:bg-white/30 transition-colors"
                  onClick={() => setChangePasswordOpen(true)}
                  type="button"
                >
                  Пароль
                </button>
                <button
                  className="px-4 py-2 rounded-md font-medium text-sm border border-white/30 bg-white/10 text-white hover:bg-white/20 active:bg-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={logoutMutation.isPending}
                  onClick={handleLogout}
                  type="button"
                >
                  {logoutMutation.isPending ? "Выход..." : "Выйти"}
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="border-b border-gray-200 bg-white">
          <div className="max-w-[120rem] mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav
              className="flex w-fit items-center gap-1 rounded-lg bg-gray-100 p-1"
              aria-label="Разделы приложения"
            >
              <NavLink className={sectionNavClass} to="/" end>
                Боты
              </NavLink>
              <NavLink className={sectionNavClass} to="/scenarios">
                Сценарии
              </NavLink>
              {canViewStudents && (
                <NavLink className={sectionNavClass} to="/students">
                  Студенты
                </NavLink>
              )}
              {canViewStudents && (
                <NavLink className={sectionNavClass} to="/feedback">
                  Обратная связь
                </NavLink>
              )}
              <NavLink className={sectionNavClass} to="/tokens">
                Токены
              </NavLink>
              {isAdmin && (
                <NavLink className={sectionNavClass} to="/users">
                  Пользователи
                </NavLink>
              )}
            </nav>
          </div>
        </div>

        <main className="max-w-[120rem] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <MainContent />
        </main>
      </div>
      <ChangePasswordModal
        onClose={() => setChangePasswordOpen(false)}
        isOpen={changePasswordOpen}
      />
      <ToastContainer />
    </>
  );
}

function App() {
  return (
    <HeroUIProvider>
      <BrowserRouter>
        <AppInner />
      </BrowserRouter>
    </HeroUIProvider>
  );
}

export default App;
